import React from "react";
import { TypedToolInvocation } from "../utils";
import { Button } from "../../../../components/ui/button";
import { CheckIcon, ReplaceIcon } from "lucide-react";
import { useProject } from "../../../../data/project";
import { Dialog, DialogContent, DialogFooter } from "../../../../components/ui/dialog";
import { ProjectOutlineView } from "../../../../components/shared/project/project-outline-view";
import { applyDiffToOutline, ProjectDiff } from "tekkr-common/dist/model/project/project-iteration";
import { useChatActions } from "../chat-subpage";

export function SuggestPlanChangesToolInvocation({
    invocation,
}: {
    invocation: TypedToolInvocation<"suggestPlanChanges">;
}) {
    const [showDiff, setShowDiff] = React.useState(false);
    const [applied, setApplied] = React.useState(false);
    const suggestionId = invocation.result?.suggestionId;
    return (
        <div
            className={
                "flex flex-row items-center gap-3 rounded-md border border-primary bg-primary/20 p-2 px-4 text-sm font-medium"
            }
        >
            {showDiff && (
                <ProjectDiffViewerDialog
                    diff={invocation.args.diff}
                    onClose={() => setShowDiff(false)}
                    suggestionId={suggestionId}
                    applied={applied}
                    onApply={() => setApplied(true)}
                />
            )}
            <ReplaceIcon className={"h-5 w-5 shrink-0"} />
            <span>
                The agent is <b>suggesting {invocation.args.diff.changes.length} changes</b> to the project plan.
            </span>
            <Button onClick={() => setShowDiff(true)} variant={"outline"} size={"sm"}>
                See changes
            </Button>
        </div>
    );
}

function ProjectDiffViewerDialog(props: {
    diff: ProjectDiff;
    onClose: () => void;
    suggestionId?: string;
    applied: boolean;
    onApply: () => void;
}) {
    const { project } = useProject();
    const chatActions = useChatActions();

    const handleApply = () => {
        if (!props.suggestionId) return;
        chatActions.sendMessage("Apply the suggested plan changes.");
        props.onApply();
        props.onClose();
    };

    return (
        <Dialog open={true} onOpenChange={props.onClose}>
            <DialogContent size={"fullScreen"} className={"grid-rows-[1fr_auto] px-4"}>
                <div className={"flex min-h-0 flex-row items-stretch"}>
                    <div className={"basis-1/2 overflow-y-auto p-8"}>
                        <h3 className={"mb-4"}>Before</h3>
                        <ProjectOutlineView plan={project.outline} diff={props.diff} />
                    </div>
                    <div className={"basis-1/2 overflow-y-auto p-8"}>
                        <h3 className={"mb-4"}>After</h3>
                        <ProjectOutlineView plan={applyDiffToOutline(project.outline, props.diff)} diff={props.diff} />
                    </div>
                </div>
                <DialogFooter className={"p-4"}>
                    <Button variant={"outline"} onClick={props.onClose}>
                        Cancel
                    </Button>
                    <Button disabled={!props.suggestionId || props.applied} onClick={handleApply}>
                        {props.applied ? (
                            <>
                                <CheckIcon className={"mr-1.5 h-4 w-4"} />
                                Applied
                            </>
                        ) : (
                            "Apply Plan"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
